import { useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/providers/auth-provider'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { reportError } from '@/lib/error-reporter'
import { subscribeToMailingList } from '@/lib/queries/onboarding'
import {
  type OrganizationInvitation,
  organizationInvitation,
} from '@/lib/queries/organization-invitations'
import { profile } from '@/lib/queries/profiles'
import { supabase } from '@/lib/supabase/client'
import { refineDisplayName } from '@/lib/validation/display-name'
import { passwordFieldsSchema } from '@/lib/validation/password'
import { useAuthActions } from '@/hooks/use-auth-actions'
import {
  useAcceptInvitation,
  useDeclineInvitations,
} from '@/hooks/use-invitation-mutations'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PageSpinner } from '@/components/ui/spinner'
import { Logo } from '@/components/logo'
import { PasswordInput } from '@/components/password-input'
import {
  AuthDivider,
  SocialSignIn,
} from '@/features/auth/components/social-sign-in'

const searchSchema = z.object({
  org_id: z.uuid().catch(''),
})

const onboardingSchema = passwordFieldsSchema.safeExtend({
  fullName: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(60, 'Name must be less than 60 characters')
    .superRefine(refineDisplayName()),
})

type OnboardingFormValues = z.infer<typeof onboardingSchema>

function AcceptInviteComponent() {
  const { user, isLoading } = useAuth()
  const navigate = useNavigate()

  // Show spinner while auth is initializing
  if (isLoading) {
    return (
      <div className='flex min-h-screen flex-col items-center justify-center'>
        <PageSpinner />
      </div>
    )
  }

  // Redirect to sign-in if not authenticated, then come back here
  if (!user) {
    navigate({
      to: '/sign-in',
      search: { redirect: `${location.pathname}${location.search}` },
      replace: true,
    })
    return null
  }

  return <AuthenticatedAcceptInvite user={user} />
}

function AuthenticatedAcceptInvite({
  user,
}: {
  user: NonNullable<ReturnType<typeof useAuth>['user']>
}) {
  const { org_id } = Route.useSearch()
  const { data: invitations } = useSuspenseQuery(
    organizationInvitation.findManyPendingByUser(user.id)
  )
  const { data: profileData } = useSuspenseQuery(profile.findOne(user.id))

  const invitation = invitations.find((invite) => invite.org_id === org_id)
  if (!invitation) {
    throw new Error('No pending invitation found for this organization.')
  }

  // Google invitees have no password to set; the app asks for a missing name.
  const signedInWithGoogle =
    user.app_metadata.providers?.includes('google') ?? false
  const needsAccountSetup =
    !profileData.onboarding_completed_at && !signedInWithGoogle

  return (
    <InvitationCard>
      {needsAccountSetup ? (
        <AccountSetup user={user} invitation={invitation} />
      ) : (
        <JoinChoice invitation={invitation} fullName={profileData.full_name} />
      )}
    </InvitationCard>
  )
}

function InvitationCard({ children }: { children: React.ReactNode }) {
  return (
    <div className='container grid h-svh max-w-none items-center justify-center'>
      <div className='mx-auto flex w-full flex-col justify-center space-y-2 py-8 sm:w-[480px] sm:p-8'>
        <div className='mb-4 flex items-center justify-center'>
          <Logo className='mr-2 w-12' />
          <h1 className='text-xl font-medium'>Lumoo</h1>
        </div>
        <Card className='gap-4'>{children}</Card>
      </div>
    </div>
  )
}

/**
 * Joining needs the invitee's say: anyone can invite any address, and the
 * person behind it may have come to Lumoo for a team of their own.
 */
function JoinChoice({
  invitation,
  fullName,
}: {
  invitation: OrganizationInvitation
  fullName: string | null
}) {
  const navigate = useNavigate()
  const accept = useAcceptInvitation()
  const decline = useDeclineInvitations()
  const isBusy = accept.isPending || decline.isPending

  const join = () =>
    accept.mutate(invitation.org_id, {
      onSuccess: () => {
        toast.success(
          fullName
            ? `Welcome to ${invitation.org_name}, ${fullName}!`
            : `Welcome to ${invitation.org_name}!`,
          { id: 'invite-welcome' }
        )
        navigate({
          to: '/org/$orgId',
          params: { orgId: invitation.org_id },
          replace: true,
        })
      },
      onError: (error) => reportError(error, { toast: error.message }),
    })

  const createOwnTeam = () =>
    decline.mutate(undefined, {
      onSuccess: () => navigate({ to: '/', replace: true }),
      onError: (error) => reportError(error, { toast: error.message }),
    })

  return (
    <>
      <CardHeader>
        <CardTitle className='text-lg tracking-tight'>
          Join {invitation.org_name}?
        </CardTitle>
        <CardDescription>
          {invitation.inviter_name} invited you to join {invitation.org_name}.
          Its members will see everything you create there.
        </CardDescription>
      </CardHeader>
      <CardContent className='flex flex-col gap-2'>
        <Button className='w-full' disabled={isBusy} onClick={join}>
          {accept.isPending && (
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
          )}
          Join {invitation.org_name}
        </Button>
        <Button
          variant='outline'
          className='w-full'
          disabled={isBusy}
          onClick={createOwnTeam}
        >
          {decline.isPending && (
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
          )}
          Create my own team instead
        </Button>
      </CardContent>
    </>
  )
}

function AccountSetup({
  user,
  invitation,
}: {
  user: NonNullable<ReturnType<typeof useAuth>['user']>
  invitation: OrganizationInvitation
}) {
  const queryClient = useQueryClient()
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  const { updatePassword } = useAuthActions()

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      fullName: '',
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (values: OnboardingFormValues) => {
    setIsSavingPassword(true)
    try {
      const result = await updatePassword(values.password)

      if (!result.success) {
        form.setError('password', {
          message: result.error!,
        })
        return
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          full_name: values.fullName,
          onboarding_completed_at: new Date().toISOString(),
        })
        .eq('id', user.id)

      if (profileError) {
        throw new Error(`Failed to update profile: ${profileError.message}`, {
          cause: profileError,
        })
      }

      if (user.email) {
        subscribeToMailingList(user.email, values.fullName)
      }

      await queryClient.invalidateQueries({ queryKey: ['profile', user.id] })
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to complete onboarding'
      reportError(error, { toast: message })
    } finally {
      setIsSavingPassword(false)
    }
  }

  return (
    <>
      <CardHeader>
        <CardTitle className='text-lg tracking-tight'>
          Welcome to Lumoo
        </CardTitle>
        <CardDescription>
          {invitation.inviter_name} has invited you to join{' '}
          {invitation.org_name}. <br />
          Set up your account first: continue with Google, or set a password.
        </CardDescription>
      </CardHeader>
      <CardContent className='flex flex-col gap-4'>
        <SocialSignIn redirectTo={`${location.pathname}${location.search}`} />
        <p className='text-muted-foreground text-meta -mt-2'>
          Use the Google account for {user.email}.
        </p>
        <AuthDivider />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='fullName'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input
                      type='text'
                      placeholder='Enter your full name'
                      autoComplete='name'
                      disabled={isSavingPassword}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder='Enter a strong password'
                      disabled={isSavingPassword}
                      autoComplete='new-password'
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Must be at least 8 characters.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='confirmPassword'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder='Confirm your password'
                      disabled={
                        isSavingPassword || !form.formState.dirtyFields.password
                      }
                      autoComplete='new-password'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type='submit'
              className='w-full'
              disabled={isSavingPassword}
            >
              {isSavingPassword && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              Continue
            </Button>
          </form>
        </Form>
      </CardContent>
    </>
  )
}

// Acceptance fails when the invitation is for another account (the usual
// case: already signed in as someone else), expired, or revoked.
function AcceptInviteError() {
  const { user } = useAuth()
  const { signOut } = useAuthActions()

  return (
    <div className='container grid h-svh max-w-none items-center justify-center'>
      <div className='mx-auto flex w-full flex-col justify-center space-y-2 py-8 sm:w-[480px] sm:p-8'>
        <div className='mb-4 flex items-center justify-center'>
          <Logo className='mr-2 w-12' />
          <h1 className='text-xl font-medium'>Lumoo</h1>
        </div>
        <Card className='gap-4'>
          <CardHeader>
            <CardTitle className='text-lg tracking-tight'>
              Invitation Unavailable
            </CardTitle>
            <CardDescription>
              {user?.email
                ? `There is no pending invitation to this organization for ${user.email}. If it was sent to another address, sign out and sign in with that account.`
                : 'This invitation has expired or was revoked. Ask the sender to invite you again.'}
            </CardDescription>
          </CardHeader>
          <CardContent className='flex flex-col gap-2'>
            {user && (
              <Button className='w-full' onClick={() => signOut()}>
                Sign out
              </Button>
            )}
            <Link
              to='/'
              className={buttonVariants({
                variant: 'outline',
                className: 'w-full',
              })}
            >
              Go to Lumoo
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/auth/accept-invite')({
  component: AcceptInviteComponent,
  errorComponent: AcceptInviteError,
  validateSearch: searchSchema,
})
